(() => {
  'use strict';

  const qs = (selector, context = document) => context.querySelector(selector);
  const qsa = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const parseJson = (element, fallback = []) => {
    try {
      return JSON.parse(element?.textContent || JSON.stringify(fallback));
    } catch (error) {
      console.error('ROVEJ JSON error', error);
      return fallback;
    }
  };

  const normalize = (value = '') => String(value)
    .toLocaleLowerCase('tr-TR')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/ı/g, 'i')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

  const money = (cents, currency = 'TRY') => new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2
  }).format((Number(cents) || 0) / 100);

  const discountPercent = (price, compareAtPrice) => {
    const current = Number(price) || 0;
    const reference = Number(compareAtPrice) || 0;
    if (reference <= current || current <= 0) return 0;
    return Math.floor(((reference - current) / reference) * 100);
  };

  const syncDiscountPrice = ({ priceNode, compareNode, discountNode, price, compareAtPrice, currency = 'TRY' }) => {
    const current = Number(price) || 0;
    const reference = Number(compareAtPrice) || 0;
    const percent = discountPercent(current, reference);
    if (priceNode) priceNode.textContent = money(current, currency);
    if (compareNode) {
      compareNode.textContent = percent ? money(reference, currency) : '';
      compareNode.hidden = !percent;
    }
    if (discountNode) {
      discountNode.textContent = percent ? `%${percent} avantaj` : '';
      discountNode.hidden = !percent;
    }
  };

  const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

  const requestJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        ...(options.body ? { 'Content-Type': 'application/json' } : {}),
        ...(options.headers || {})
      },
      ...options
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(result.description || result.message || 'İşlem tamamlanamadı. Lütfen tekrar deneyin.');
    }
    return result;
  };

  const cartPanel = (() => {
    const panel = qs('[data-rovej-cart-panel]');
    if (!panel) return null;

    const dialog = qs('.rovej-cart-panel__dialog', panel);
    const itemsNode = qs('[data-rovej-cart-items]', panel);
    const emptyNode = qs('[data-rovej-cart-empty]', panel);
    const filledNode = qs('[data-rovej-cart-filled]', panel);
    const footerNode = qs('[data-rovej-cart-footer]', panel);
    const loadingNode = qs('[data-rovej-cart-loading]', panel);
    const totalNode = qs('[data-rovej-cart-total]', panel);
    const countNode = qs('[data-rovej-cart-heading-count]', panel);
    const agreement = qs('[data-rovej-cart-agreement]', panel);
    const checkoutButton = qs('[data-rovej-checkout]', panel);
    const errorNode = qs('[data-rovej-cart-error]', panel);
    const upsellsNode = qs('[data-rovej-upsells]', panel);
    const upsellCards = qsa('[data-rovej-upsell-card]', panel);
    let lastFocus = null;
    let currentCart = null;
    let closeTimer = null;
    let catalogPromise = null;
    const productSearchPromises = new Map();
    let upsellHydrationPromise = null;

    const setLoading = (state) => {
      if (loadingNode) loadingNode.hidden = !state;
      panel.classList.toggle('is-loading', state);
    };

    const setError = (message = '') => {
      if (!errorNode) return;
      errorNode.textContent = message;
      errorNode.hidden = !message;
    };

    const updateHeaderCount = (count) => {
      qsa('[data-rovej-cart-count]').forEach((node) => {
        node.textContent = count;
        node.hidden = count === 0;
      });
    };

    const propertyMarkup = (properties = {}) => Object.entries(properties)
      .filter(([key, value]) => value && !key.startsWith('_'))
      .map(([key, value]) => `<span>${escapeHtml(key)}: ${escapeHtml(value)}</span>`)
      .join('');

    const itemMarkup = (item, currency) => {
      const originalImage = item.featured_image?.url || item.image || '';
      const imageUrl = originalImage ? `${originalImage}${originalImage.includes('?') ? '&' : '?'}width=180` : '';
      const image = imageUrl
        ? `<img src="${escapeHtml(imageUrl)}" width="90" height="112" loading="lazy" alt="${escapeHtml(item.product_title)}">`
        : '<span class="rovej-cart-item__placeholder" aria-hidden="true"></span>';
      const variant = item.variant_title && item.variant_title !== 'Default Title'
        ? `<p>${escapeHtml(item.variant_title)}</p>`
        : '';
      return `
        <li class="rovej-cart-item" data-cart-item-key="${escapeHtml(item.key)}">
          <div class="rovej-cart-item__image">${image}</div>
          <div class="rovej-cart-item__body">
            <div class="rovej-cart-item__top">
              <div>
                <h3>${escapeHtml(item.product_title)}</h3>
                ${variant}
                <div class="rovej-cart-item__properties">${propertyMarkup(item.properties)}</div>
              </div>
              <strong>${money(item.final_line_price, currency)}</strong>
            </div>
            <div class="rovej-cart-item__actions">
              <div class="rovej-cart-quantity" aria-label="Ürün adedi">
                <button type="button" data-rovej-cart-change="${escapeHtml(item.key)}" data-quantity="${Math.max(0, item.quantity - 1)}" aria-label="Bir azalt">−</button>
                <span>${item.quantity}</span>
                <button type="button" data-rovej-cart-change="${escapeHtml(item.key)}" data-quantity="${item.quantity + 1}" aria-label="Bir artır">+</button>
              </div>
              <button class="rovej-cart-item__remove" type="button" data-rovej-cart-change="${escapeHtml(item.key)}" data-quantity="0">Kaldır</button>
            </div>
          </div>
        </li>`;
    };

    const syncUpsellButtons = (cart = currentCart) => {
      qsa('[data-rovej-upsell-add]', panel).forEach((button) => {
        const card = button.closest('[data-rovej-upsell-card]');
        const variantId = Number(button.dataset.variantId || 0);
        const isInCart = Boolean(variantId && cart?.items?.some((item) => Number(item.variant_id) === variantId));
        const isResolved = button.dataset.resolved === 'true';
        const isAvailable = button.dataset.available === 'true';
        if (card) card.hidden = isInCart;
        if (isInCart) {
          button.textContent = 'Sepette ✓';
          button.disabled = true;
          return;
        }
        if (!isResolved) {
          button.textContent = 'Yükleniyor…';
          button.disabled = true;
          return;
        }
        if (button.dataset.lookupFailed === 'true') {
          button.textContent = 'Şu an kullanılamıyor';
          button.disabled = true;
          return;
        }
        button.textContent = isAvailable ? '+ Sepete Ekle' : 'Tükendi';
        button.disabled = !variantId || !isAvailable;
      });
      if (upsellsNode) {
        upsellsNode.hidden = upsellCards.length > 0 && upsellCards.every((card) => card.hidden);
      }
    };

    const loadProductCatalog = () => {
      if (!catalogPromise) {
        catalogPromise = requestJson('/products.json?limit=250', { cache: 'no-store' })
          .then((result) => Array.isArray(result.products) ? result.products : [])
          .catch(() => []);
      }
      return catalogPromise;
    };

    const productByHandle = async (handle = '') => {
      const cleanHandle = String(handle).split('/').filter(Boolean).pop();
      if (!cleanHandle) return null;
      try {
        return await requestJson(`/products/${encodeURIComponent(cleanHandle)}.js`, { cache: 'no-store' });
      } catch (error) {
        return null;
      }
    };

    const searchProducts = (term = '') => {
      const cleanTerm = String(term).trim();
      if (!cleanTerm) return Promise.resolve([]);
      if (!productSearchPromises.has(cleanTerm)) {
        const params = new URLSearchParams({
          q: cleanTerm,
          'resources[type]': 'product',
          'resources[limit]': '10',
          'resources[options][unavailable_products]': 'show'
        });
        productSearchPromises.set(cleanTerm, requestJson(`/search/suggest.json?${params.toString()}`, { cache: 'no-store' })
          .then((result) => result?.resources?.results?.products || [])
          .catch(() => []));
      }
      return productSearchPromises.get(cleanTerm);
    };

    const productHandleFromRecord = (product = {}) => {
      if (product.handle) return product.handle;
      const match = String(product.url || '').match(/\/products\/([^/?#]+)/);
      return match?.[1] ? decodeURIComponent(match[1]) : '';
    };

    const bestProductMatch = (products, card) => {
      const keywords = String(card.dataset.productKeywords || '')
        .split('|')
        .map((keyword) => normalize(keyword))
        .filter(Boolean);
      if (!keywords.length) return null;

      return products
        .map((product) => {
          const haystack = normalize([
            product.title,
            productHandleFromRecord(product),
            product.url,
            product.product_type,
            Array.isArray(product.tags) ? product.tags.join(' ') : product.tags
          ].filter(Boolean).join(' '));
          const score = keywords.reduce((total, keyword) => total + (haystack.includes(keyword) ? 1 : 0), 0);
          return { product, score };
        })
        .filter((candidate) => candidate.score >= 2)
        .sort((left, right) => right.score - left.score)[0]?.product || null;
    };

    const resolveUpsellProduct = async (card) => {
      const handles = [card.dataset.productHandle, ...String(card.dataset.productHandles || '').split('|')]
        .map((handle) => String(handle || '').trim())
        .filter((handle, index, values) => handle && values.indexOf(handle) === index);
      for (const handle of handles) {
        const directProduct = await productByHandle(handle);
        if (directProduct?.variants?.length) return directProduct;
      }

      const catalog = await loadProductCatalog();
      const catalogMatch = bestProductMatch(catalog, card);
      if (catalogMatch) return await productByHandle(productHandleFromRecord(catalogMatch)) || catalogMatch;

      const searchResults = await searchProducts(card.dataset.productSearch);
      const searchMatch = bestProductMatch(searchResults, card);
      if (!searchMatch) return null;
      return await productByHandle(productHandleFromRecord(searchMatch)) || searchMatch;
    };

    const priceInCents = (value) => {
      if (typeof value === 'string' && /[.,]/.test(value)) {
        return Math.round(Number(value.replace(',', '.')) * 100);
      }
      return Number(value) || 0;
    };

    const hydrateUpsells = () => {
      if (upsellHydrationPromise) return upsellHydrationPromise;
      upsellHydrationPromise = Promise.all(upsellCards.map(async (card) => {
        const button = qs('[data-rovej-upsell-add]', card);
        const priceNode = qs('[data-rovej-upsell-price]', card);
        const compareNode = qs('[data-rovej-upsell-compare-price]', card);
        const discountNode = qs('[data-rovej-upsell-discount]', card);
        if (!button || Number(button.dataset.variantId || 0)) {
          if (button) button.dataset.resolved = 'true';
          return;
        }

        const product = await resolveUpsellProduct(card);
        const variants = Array.isArray(product?.variants) ? product.variants : [];
        const variant = variants.find((candidate) => candidate.available !== false) || variants[0];
        if (!variant?.id) {
          button.dataset.resolved = 'true';
          button.dataset.available = 'false';
          button.dataset.lookupFailed = 'true';
          button.textContent = 'Şu an kullanılamıyor';
          button.disabled = true;
          if (priceNode) priceNode.textContent = 'Geçici olarak kullanılamıyor';
          return;
        }

        button.dataset.variantId = String(variant.id);
        button.dataset.available = String(variant.available !== false);
        button.dataset.resolved = 'true';
        syncDiscountPrice({
          priceNode,
          compareNode,
          discountNode,
          price: priceInCents(variant.price),
          compareAtPrice: priceInCents(variant.compare_at_price),
          currency: currentCart?.currency || 'TRY'
        });
      })).finally(() => syncUpsellButtons());
      return upsellHydrationPromise;
    };

    const render = (cart) => {
      currentCart = cart;
      const isEmpty = cart.item_count === 0;
      updateHeaderCount(cart.item_count);
      if (countNode) countNode.textContent = isEmpty ? '' : `• ${cart.item_count} ürün`;
      if (emptyNode) emptyNode.hidden = !isEmpty;
      if (filledNode) filledNode.hidden = isEmpty;
      if (footerNode) footerNode.hidden = isEmpty;
      if (itemsNode) itemsNode.innerHTML = cart.items.map((item) => itemMarkup(item, cart.currency || 'TRY')).join('');
      if (totalNode) totalNode.textContent = money(cart.total_price, cart.currency || 'TRY');
      if (checkoutButton) {
        checkoutButton.textContent = 'Satın al';
        checkoutButton.disabled = isEmpty || !agreement?.checked;
      }
      syncUpsellButtons(cart);
      setError();
    };

    const refresh = async () => {
      setLoading(true);
      try {
        const cart = await requestJson('/cart.js', { cache: 'no-store' });
        render(cart);
        return cart;
      } finally {
        setLoading(false);
      }
    };

    const open = async (trigger = document.activeElement) => {
      lastFocus = trigger instanceof HTMLElement ? trigger : null;
      window.clearTimeout(closeTimer);
      panel.hidden = false;
      document.body.classList.add('rovej-cart-open');
      requestAnimationFrame(() => panel.classList.add('is-open'));
      try {
        await Promise.all([hydrateUpsells(), refresh()]);
      } catch (error) {
        setError(error.message);
      }
      dialog?.focus({ preventScroll: true });
    };

    const close = () => {
      panel.classList.remove('is-open');
      document.body.classList.remove('rovej-cart-open');
      closeTimer = window.setTimeout(() => { panel.hidden = true; }, 260);
      lastFocus?.focus?.({ preventScroll: true });
    };

    const add = async (items, trigger) => {
      trigger?.setAttribute('aria-busy', 'true');
      setError();
      try {
        await requestJson('/cart/add.js', {
          method: 'POST',
          body: JSON.stringify({ items })
        });
        await open(trigger);
      } finally {
        trigger?.removeAttribute('aria-busy');
      }
    };

    qsa('[data-rovej-cart-close]', panel).forEach((button) => button.addEventListener('click', close));

    panel.addEventListener('click', async (event) => {
      const changeButton = event.target.closest('[data-rovej-cart-change]');
      if (changeButton) {
        changeButton.disabled = true;
        setLoading(true);
        try {
          const cart = await requestJson('/cart/change.js', {
            method: 'POST',
            body: JSON.stringify({ id: changeButton.dataset.rovejCartChange, quantity: Number(changeButton.dataset.quantity) })
          });
          render(cart);
        } catch (error) {
          setError(error.message);
        } finally {
          setLoading(false);
          changeButton.disabled = false;
        }
        return;
      }

      const upsellButton = event.target.closest('[data-rovej-upsell-add]');
      if (upsellButton) {
        const variantId = Number(upsellButton.dataset.variantId || 0);
        if (!variantId) return;
        try {
          await add([{ id: variantId, quantity: 1 }], upsellButton);
        } catch (error) {
          setError(error.message);
        }
      }
    });

    agreement?.addEventListener('change', () => {
      if (checkoutButton) checkoutButton.disabled = !agreement.checked || !currentCart?.item_count;
      if (agreement.checked) setError();
    });

    checkoutButton?.addEventListener('click', async () => {
      if (!agreement?.checked) {
        setError('Siparişe geçmek için sözleşmeleri okuyup onaylamalısınız.');
        agreement?.focus();
        return;
      }
      checkoutButton.disabled = true;
      checkoutButton.setAttribute('aria-busy', 'true');
      setError();
      try {
        await requestJson('/cart/update.js', {
          method: 'POST',
          body: JSON.stringify({
            attributes: {
              'Sözleşme onayı': 'Ön Bilgilendirme Formu ve Mesafeli Satış Sözleşmesi okundu ve onaylandı'
            }
          })
        });
        window.location.assign('/checkout');
      } catch (error) {
        setError(error.message);
        checkoutButton.disabled = false;
        checkoutButton.removeAttribute('aria-busy');
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) close();
    });

    qsa('[data-rovej-cart-trigger]').forEach((trigger) => trigger.addEventListener('click', (event) => {
      event.preventDefault();
      open(trigger);
    }));

    return { add, open, close, refresh, render, hydrateUpsells };
  })();

  const initHeader = () => {
    const toggle = qs('[data-rovej-menu-toggle]');
    const nav = qs('[data-rovej-mobile-nav]');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', () => {
      const next = toggle.getAttribute('aria-expanded') !== 'true';
      toggle.setAttribute('aria-expanded', String(next));
      nav.classList.toggle('is-open', next);
    });
    qsa('a', nav).forEach((link) => link.addEventListener('click', () => {
      toggle.setAttribute('aria-expanded', 'false');
      nav.classList.remove('is-open');
    }));
  };

  const initHeroVideo = (root) => {
    const video = qs('[data-rovej-hero-video]', root);
    if (!video) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      video.pause();
      video.removeAttribute('autoplay');
    }
  };

  const initSizeFinder = (root) => {
    const finder = qs('[data-rovej-size-finder]', root);
    if (!finder) return;

    const dialog = qs('.rovej-size-finder__dialog', finder);
    const form = qs('[data-rovej-size-finder-form]', finder);
    const results = qs('[data-rovej-size-finder-results]', finder);
    const summary = qs('[data-rovej-size-finder-summary]', finder);
    const cards = qs('[data-rovej-size-finder-cards]', finder);
    const error = qs('[data-rovej-size-finder-error]', finder);
    const hipInput = qs('[data-rovej-size-hip]', finder);
    const waistInput = qs('[data-rovej-size-waist]', finder);
    const sizeSystem = [
      { value: '1', code: 'B1', min: 88, max: 94, productHip: 99 },
      { value: '2', code: 'B2', min: 95, max: 101, productHip: 106 },
      { value: '3', code: 'B3', min: 102, max: 108, productHip: 113 },
      { value: '4', code: 'B4', min: 109, max: 115, productHip: 120 },
      { value: '5', code: 'B5', min: 116, max: 122, productHip: 127 },
      { value: '6', code: 'B6', min: 123, max: 129, productHip: 134 }
    ];
    let targetRow = null;
    let opener = null;

    const setError = (message = '') => {
      if (!error) return;
      error.textContent = message;
      error.hidden = !message;
    };

    const showForm = () => {
      if (form) form.hidden = false;
      if (results) results.hidden = true;
      setError();
    };

    const close = () => {
      finder.hidden = true;
      document.body.classList.remove('rovej-size-finder-open');
      opener?.focus();
    };

    const open = (button) => {
      opener = button;
      targetRow = button.closest('[data-rovej-item-row]');
      showForm();
      finder.hidden = false;
      document.body.classList.add('rovej-size-finder-open');
      window.requestAnimationFrame(() => {
        if (hipInput?.value) dialog?.focus();
        else hipInput?.focus();
      });
    };

    const outsideRange = (hip, waist, reference) => {
      const reason = reference < 88 ? '88 cm’nin altında' : '129 cm’nin üzerinde';
      if (summary) {
        summary.innerHTML = `
          <span class="rovej-size-finder__summary-label">Ölçü kontrolü</span>
          <h3>Mevcut beden aralığının dışında</h3>
          <p>En geniş çevre ölçün <strong>${reference} cm</strong> ve bu değer ${reason}. Yanlış yönlendirmemek için otomatik beden önermiyoruz.</p>
          <small>Basen: ${hip} cm · Bel: ${waist} cm · Mevcut aralık: 88–129 cm</small>
        `;
      }
      if (cards) {
        cards.innerHTML = '<div class="rovej-size-finder__support">Ölçünü yeniden kontrol et. Doğruysa sana özel yönlendirme için bizimle iletişime geç.</div>';
      }
      if (form) form.hidden = true;
      if (results) results.hidden = false;
    };

    const resultCard = (size, type, reference, isPromoted, layer) => {
      const difference = Number((size.productHip - reference).toFixed(1));
      const balancedCopy = isPromoted
        ? 'Kalın katman ve aralığın üst noktasında olduğun için bir üst bedeni öneriyoruz.'
        : 'Vücuduna daha dengeli oturması için ilk önerimiz.';
      const looseCopy = layer === 'thick'
        ? 'Kalın pantolon üzerinde daha dökümlü görünüm ve daha fazla rahatlık için.'
        : 'Daha dökümlü görünüm ve daha serbest hareket hissi için.';
      const badge = type === 'balanced' ? 'İlk önerimiz' : 'Daha salaş';
      const title = type === 'balanced' ? 'Dengeli oturuş' : 'Rahat oturuş';
      const copy = type === 'balanced' ? balancedCopy : looseCopy;
      return `
        <article class="rovej-size-result-card${type === 'balanced' ? ' is-primary' : ''}">
          <span class="rovej-size-result-card__badge">${badge}</span>
          <div class="rovej-size-result-card__heading"><strong>${size.code}</strong><span>${title}</span></div>
          <p>${copy}</p>
          <dl>
            <div><dt>Vücut basen aralığı</dt><dd>${size.min}–${size.max} cm</dd></div>
            <div><dt>Bitmiş ürün baseni</dt><dd>${size.productHip} cm</dd></div>
            <div><dt>En geniş ölçüne göre fark</dt><dd>${difference} cm</dd></div>
            <div><dt>Ürün boyu</dt><dd>Ön 40 · Arka 43 · Yan 39 cm</dd></div>
          </dl>
          <button class="rovej-size-result-card__select" type="button" data-rovej-size-result-select="${size.value}">${size.code} bedenini seç</button>
        </article>
      `;
    };

    const calculate = () => {
      const hip = Number(hipInput?.value || '');
      const waist = Number(waistInput?.value || '');
      const layer = qs('input[name="layer"]:checked', form)?.value || 'thin';
      if (!Number.isFinite(hip) || hip < 70 || hip > 170 || !Number.isFinite(waist) || waist < 50 || waist > 170) {
        setError('Lütfen basen ve bel ölçülerini santimetre olarak eksiksiz gir.');
        return;
      }

      setError();
      const reference = Math.max(hip, waist);
      if (reference < 88 || reference > 129) {
        outsideRange(hip, waist, reference);
        return;
      }

      const baseIndex = sizeSystem.findIndex((size) => reference <= size.max);
      if (baseIndex < 0) {
        setError('Ölçünü santimetre olarak yeniden gir.');
        return;
      }

      const betweenRanges = reference < sizeSystem[baseIndex].min;
      const thickAtUpperEdge = layer === 'thick' && reference > sizeSystem[baseIndex].max - 2;
      const balancedIndex = thickAtUpperEdge && baseIndex < sizeSystem.length - 1 ? baseIndex + 1 : baseIndex;
      const looseIndex = balancedIndex < sizeSystem.length - 1 ? balancedIndex + 1 : null;
      const usedWaist = waist > hip;
      const limitedAtTop = layer === 'thick' && baseIndex === sizeSystem.length - 1 && thickAtUpperEdge;

      if (summary) {
        summary.innerHTML = `
          <span class="rovej-size-finder__summary-label">Ölçü sonucun</span>
          <h3>${usedWaist ? 'En geniş çevreni esas aldık' : 'Basen ölçünü esas aldık'}</h3>
          <p>${usedWaist
            ? `Bel ölçün baseninden geniş olduğu için hesabı <strong>${waist} cm</strong> üzerinden yaptık.`
            : `<strong>${hip} cm basen</strong> ana ölçün; ${waist} cm bel ölçün de hesaba dahil edildi.`}</p>
          ${thickAtUpperEdge && !limitedAtTop ? '<small>Kalın katman tercihin aralığın üst sınırında olduğu için dengeli öneriyi bir beden büyüttük.</small>' : ''}
          ${betweenRanges ? '<small>İki beden aralığı arasında kaldığın için büyük olanı esas aldık.</small>' : ''}
          ${limitedAtTop ? '<small>Kalın katmanla mevcut beden aralığının üst sınırındasın; satın almadan önce destek almanı öneririz.</small>' : ''}
        `;
      }

      if (cards) {
        cards.innerHTML = resultCard(sizeSystem[balancedIndex], 'balanced', reference, balancedIndex !== baseIndex, layer)
          + (looseIndex === null ? '' : resultCard(sizeSystem[looseIndex], 'loose', reference, false, layer));
      }
      if (form) form.hidden = true;
      if (results) results.hidden = false;
      qs('[data-rovej-size-result-select]', cards)?.focus();
      window.dataLayer?.push({
        event: 'rovej_size_recommendation',
        recommendation: sizeSystem[balancedIndex].code
      });
    };

    qsa('[data-rovej-size-finder-open]', root).forEach((button) => button.addEventListener('click', () => open(button)));
    qsa('[data-rovej-size-finder-close]', finder).forEach((button) => button.addEventListener('click', close));
    qs('[data-rovej-size-finder-edit]', finder)?.addEventListener('click', () => {
      showForm();
      hipInput?.focus();
    });
    form?.addEventListener('submit', (event) => {
      event.preventDefault();
      calculate();
    });
    cards?.addEventListener('click', (event) => {
      const button = event.target.closest('[data-rovej-size-result-select]');
      if (!button) return;
      const sizeSelects = targetRow ? [qs('[data-rovej-size]', targetRow)] : qsa('[data-rovej-size]', root);
      const choices = sizeSelects
        .filter(Boolean)
        .map((sizeSelect) => ({
          sizeSelect,
          option: qsa('option', sizeSelect).find((candidate) => candidate.value === button.dataset.rovejSizeResultSelect)
        }));
      qsa('.rovej-size-finder__stock-warning', summary).forEach((warning) => warning.remove());
      if (!choices.length || choices.some(({ option }) => !option || option.disabled)) {
        if (summary) summary.insertAdjacentHTML('beforeend', '<p class="rovej-size-finder__stock-warning">Bu beden seçtiğin renklerden birinde şu an mevcut değil. Rengi değiştirerek tekrar dene.</p>');
        return;
      }
      choices.forEach(({ sizeSelect, option }) => {
        sizeSelect.value = option.value;
        sizeSelect.dispatchEvent(new Event('change', { bubbles: true }));
      });
      close();
      if (targetRow) {
        choices[0].sizeSelect.focus();
      } else {
        const formMessage = qs('[data-rovej-form-message]', root);
        if (formMessage) {
          formMessage.textContent = `B${button.dataset.rovejSizeResultSelect} beden önerin hazır. Paketini ve renklerini seç.`;
          formMessage.dataset.success = 'true';
        }
        qs('#rovej-offers', root)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      window.dataLayer?.push({ event: 'rovej_size_apply', size: `B${button.dataset.rovejSizeResultSelect}` });
    });

    finder.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = qsa('button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])', dialog)
        .filter((element) => !element.hidden && element.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  const initOffers = (root) => {
    const offers = qsa('[data-rovej-offer]', root);
    const addButton = qs('[data-rovej-add]', root);
    const sticky = qs('[data-rovej-sticky-buy]', root);
    const message = qs('[data-rovej-form-message]', root);
    const variants = parseJson(qs('[data-rovej-variants]', root));
    const variantsBySku = new Map(variants.map((variant) => [String(variant.sku || '').toUpperCase(), variant]));
    const colorNames = { S: 'siyah', L: 'lacivert', B: 'beyaz' };
    let activeOffer = null;

    const matchesSize = (value, size) => {
      const normalized = normalize(value);
      const compact = normalized.replace(/\s/g, '');
      if (compact === size || compact === `beden${size}` || compact === `size${size}`) return true;
      const numbers = normalized.match(/\d+/g) || [];
      return numbers.length === 1 && numbers[0] === size;
    };

    const findVariant = (colorCode, size) => {
      const skuVariant = variantsBySku.get(`KE20-${colorCode}${size}`);
      if (skuVariant) return skuVariant;
      const colorName = colorNames[colorCode];
      return variants.find((variant) => {
        const values = [...(Array.isArray(variant.options) ? variant.options : []), variant.title || ''];
        const hasColor = values.some((value) => normalize(value).split(' ').includes(colorName));
        const hasSize = values.some((value) => matchesSize(value, size));
        return hasColor && hasSize;
      });
    };

    const setMessage = (text = '', success = false) => {
      if (!message) return;
      message.textContent = text;
      message.dataset.success = String(success);
    };

    const updateRowAvailability = (row) => {
      const color = qs('[data-rovej-color]', row);
      const size = qs('[data-rovej-size]', row);
      if (!color || !size) return;
      qsa('option', size).forEach((option) => {
        if (!option.value) return;
        const variant = color.value ? findVariant(color.value, option.value) : null;
        option.disabled = Boolean(color.value) && (!variant || variant.available === false);
      });
      if (size.selectedOptions[0]?.disabled) size.value = '';
    };

    const closeOffer = (offer, reset = true) => {
      const trigger = qs('[data-rovej-offer-trigger]', offer);
      const panel = qs('[data-rovej-offer-panel]', offer);
      offer.classList.remove('is-open');
      trigger?.setAttribute('aria-expanded', 'false');
      if (panel) panel.hidden = true;
      if (reset) qsa('select', offer).forEach((select) => { select.selectedIndex = 0; });
      if (activeOffer === offer) activeOffer = null;
      setMessage();
    };

    const openOffer = (offer) => {
      offers.filter((candidate) => candidate !== offer).forEach((candidate) => closeOffer(candidate));
      const trigger = qs('[data-rovej-offer-trigger]', offer);
      const panel = qs('[data-rovej-offer-panel]', offer);
      offer.classList.add('is-open');
      trigger?.setAttribute('aria-expanded', 'true');
      if (panel) panel.hidden = false;
      activeOffer = offer;
      setMessage();
      window.dataLayer?.push({ event: 'rovej_offer_select', offer: offer.dataset.offerCount });
    };

    offers.forEach((offer) => {
      qs('[data-rovej-offer-trigger]', offer)?.addEventListener('click', () => {
        if (offer.classList.contains('is-open')) closeOffer(offer);
        else openOffer(offer);
      });
      qsa('[data-rovej-item-row]', offer).forEach((row) => {
        qs('[data-rovej-color]', row)?.addEventListener('change', () => {
          updateRowAvailability(row);
          setMessage();
        });
        qs('[data-rovej-size]', row)?.addEventListener('change', () => setMessage());
      });
    });

    const buildItems = () => {
      if (!variants.length) {
        setMessage('Satış ürünü henüz bu sayfaya bağlanmamış. Lütfen mağaza yönetimine bildirin.');
        return null;
      }
      if (!activeOffer) {
        setMessage('Devam etmek için önce bir paket seç.');
        qs('#rovej-offers')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        qs('[data-rovej-offer-trigger]', offers[0])?.focus();
        return null;
      }

      const selects = qsa('select', activeOffer);
      for (const select of selects) {
        if (!select.value) {
          setMessage('Her ürün için renk ve beden seçmelisin.');
          select.focus();
          return null;
        }
      }

      const rows = qsa('[data-rovej-item-row]', activeOffer);
      const offerCount = activeOffer.dataset.offerCount;
      const offerLabel = activeOffer.dataset.offerLabel;
      const items = rows.map((row, index) => {
        const color = qs('[data-rovej-color]', row);
        const size = qs('[data-rovej-size]', row);
        const variant = findVariant(color.value, size.value);
        if (!variant || variant.available === false) return null;
        return {
          id: Number(variant.id),
          quantity: 1,
          properties: {
            'ROVEJ Teklifi': offerLabel,
            'Ürün': `${index + 1}/${offerCount}`,
            'Renk': color.options[color.selectedIndex].text,
            'Beden': size.options[size.selectedIndex].text,
            '_rovej_offer_count': offerCount
          }
        };
      });

      if (items.some((item) => item === null)) {
        setMessage('Bu renk ve beden eşleşmesi şu an kullanılamıyor. Lütfen başka bir seçim yap.');
        return null;
      }
      setMessage();
      return items;
    };

    const submit = async (button) => {
      const items = buildItems();
      if (!items?.length || !cartPanel) return;
      try {
        await cartPanel.add(items, button);
        setMessage('Seçimlerin sepete eklendi.', true);
        window.dataLayer?.push({
          event: 'add_to_cart',
          rovej_offer: activeOffer.dataset.offerCount,
          value: Number(activeOffer.dataset.offerPrice || 0),
          currency: 'TRY'
        });
      } catch (error) {
        console.error('ROVEJ add to cart error', error);
        setMessage(error.message || 'Sepete eklenemedi. Lütfen tekrar dene.');
      }
    };

    addButton?.addEventListener('click', () => submit(addButton));
    sticky?.addEventListener('click', () => {
      if (!activeOffer) {
        qs('#rovej-offers')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      submit(sticky);
    });

    if (addButton && sticky && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(([entry]) => {
        sticky.closest('.rovej-sticky-buy')?.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0 });
      observer.observe(addButton);
    }
  };

  const initProductDetail = (root) => {
    const mainImage = qs('[data-rovej-product-image]', root);
    const thumbnails = qsa('[data-rovej-product-thumbnail]', root);
    const variantSelect = qs('[data-rovej-product-variant]', root);
    const priceNode = qs('[data-rovej-product-price]', root);
    const compareNode = qs('[data-rovej-product-compare-price]', root);
    const discountNode = qs('[data-rovej-product-discount]', root);
    const addButton = qs('[data-rovej-product-add]', root);
    const statusNode = qs('[data-rovej-product-status]', root);

    thumbnails.forEach((thumbnail) => thumbnail.addEventListener('click', () => {
      if (!mainImage || !thumbnail.dataset.imageSrc) return;
      mainImage.src = thumbnail.dataset.imageSrc;
      mainImage.alt = thumbnail.dataset.imageAlt || mainImage.alt;
      thumbnails.forEach((candidate) => candidate.setAttribute('aria-current', String(candidate === thumbnail)));
    }));

    const syncVariant = () => {
      const option = variantSelect?.selectedOptions?.[0];
      if (!option) return;
      syncDiscountPrice({
        priceNode,
        compareNode,
        discountNode,
        price: option.dataset.price,
        compareAtPrice: option.dataset.compareAtPrice,
        currency: window.Shopify?.currency?.active || 'TRY'
      });
      if (addButton) {
        const available = option.dataset.available === 'true';
        addButton.disabled = !available;
        addButton.textContent = available ? 'Sepete Ekle' : 'Tükendi';
      }
      if (statusNode) statusNode.textContent = '';
    };

    variantSelect?.addEventListener('change', syncVariant);
    syncVariant();

    addButton?.addEventListener('click', async () => {
      const variantId = Number(variantSelect?.value || addButton.dataset.variantId || 0);
      if (!variantId || !cartPanel) return;
      if (statusNode) statusNode.textContent = '';
      try {
        await cartPanel.add([{ id: variantId, quantity: 1 }], addButton);
      } catch (error) {
        if (statusNode) statusNode.textContent = error.message || 'Ürün sepete eklenemedi. Lütfen tekrar deneyin.';
      }
    });
  };

  const init = () => {
    initHeader();
    qsa('[data-rovej-storefront]').forEach((root) => {
      initHeroVideo(root);
      initSizeFinder(root);
      initOffers(root);
    });
    qsa('[data-rovej-product-detail]').forEach((root) => initProductDetail(root));
    if (cartPanel) {
      Promise.all([cartPanel.hydrateUpsells(), cartPanel.refresh()])
        .catch((error) => console.error('ROVEJ cart init error', error));
    }
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
